import React, { useState } from "react";
import { openOnRowClick } from '../../../utils/rowClick';

const PendingApprovalRow = ({ approval, onApprove, onReject, onViewProfile, loading }) => {
  const [showDocModal, setShowDocModal] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) {
      return `${diffMins} minutes ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const fullName = `${approval.firstName} ${approval.lastName}`;
  const role = approval.role.charAt(0).toUpperCase() + approval.role.slice(1);
  const status = approval.registrationStatus.charAt(0).toUpperCase() + approval.registrationStatus.slice(1);

  const doc = approval.photoIdDocument;
  const docUrl = doc?.documentUrl;
  const fileType = doc?.fileType?.toLowerCase() || '';

  const isImage = fileType === 'image' || /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(docUrl || '');
  const isPdf = fileType === 'pdf' || /\.pdf$/i.test(docUrl || '');
  const isCsv = fileType === 'csv' || /\.(csv|xlsx)$/i.test(docUrl || '');

  return (
    <>
      <tr onClick={openOnRowClick(() => onViewProfile(approval._id, approval.role))} className="cursor-pointer border-t border-jp-line hover:bg-jp-section text-center">
        <td className="px-4 py-3 text-xs text-jp-ink text-center">{fullName}</td>
        <td className="px-4 py-3 text-xs text-jp-ink text-center">{approval.email}</td>
        <td className="px-4 py-3 text-xs text-jp-ink text-center">{role}</td>
        <td className="px-4 py-3 text-xs text-center">
          {docUrl ? (
            <button
              onClick={() => setShowDocModal(true)}
              className="px-3 py-1 rounded-full border border-cyan-500 text-[11px] text-jp-accent-ink bg-transparent hover:bg-cyan-900/30 transition-colors"
            >
              View Doc
            </button>
          ) : (
            <span className="text-jp-muted text-[11px]">No Document</span>
          )}
        </td>
        <td className="px-4 py-3 text-xs text-jp-ink text-center">{formatDate(approval.createdAt)}</td>
        <td className="px-4 py-3 text-xs text-jp-ink text-center">{status}</td>
        <td className="px-4 py-3 text-xs text-center">
          <div className="flex items-center gap-3 justify-center">
            <button
              onClick={() => onViewProfile(approval._id, approval.role)}
              className="px-4 py-1 rounded-full border border-jp-line text-[11px] text-jp-ink bg-transparent hover:bg-jp-line transition-colors"
            >
              View profile
            </button>
            <button
              onClick={() => onApprove(approval._id)}
              disabled={loading}
              className="px-4 py-1 rounded-full bg-sky-600 text-[11px] text-white hover:bg-sky-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Approving...' : 'Approve'}
            </button>
            <button
              onClick={() => onReject(approval._id, fullName)}
              disabled={loading}
              className="px-4 py-1 rounded-full bg-red-600 text-[11px] text-white hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Rejecting...' : 'Reject'}
            </button>
          </div>
        </td>
      </tr>

      {/* Document View Modal */}
      {showDocModal && docUrl && (
        <tr>
          <td colSpan="7" className="p-0">
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
              onClick={() => setShowDocModal(false)}
            >
              <div
                className="relative bg-jp-card border border-jp-line rounded-2xl p-4 max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-jp-ink">
                    Document - {fullName}
                  </h3>
                  <div className="flex items-center gap-3">
                    <a
                      href={docUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={isCsv}
                      className="px-3 py-1 rounded-full bg-cyan-600 text-[11px] text-white hover:bg-cyan-500 transition-colors"
                    >
                      {isCsv ? 'Download File' : 'Open in New Tab'}
                    </a>
                    <button
                      onClick={() => setShowDocModal(false)}
                      className="text-jp-muted hover:text-jp-ink transition-colors"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Document Content */}
                <div className="flex-1 overflow-auto rounded-lg bg-jp-section flex items-center justify-center min-h-[400px]">
                  {isImage && (
                    <img
                      src={docUrl}
                      alt="Verification Document"
                      className="max-w-full max-h-[70vh] object-contain rounded-lg"
                    />
                  )}
                  {isPdf && (
                    <iframe
                      src={docUrl}
                      title="Document Preview"
                      className="w-full h-[70vh] rounded-lg"
                    />
                  )}
                  {isCsv && (
                    <div className="text-center p-8">
                      <svg className="w-16 h-16 text-jp-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-jp-muted text-sm mb-1">CSV / Excel File</p>
                      <p className="text-jp-muted text-xs mb-4">This file type cannot be previewed in browser</p>
                      <a
                        href={docUrl}
                        download
                        className="px-4 py-2 rounded-full bg-cyan-600 text-xs text-white hover:bg-cyan-500 transition-colors"
                      >
                        Download File
                      </a>
                    </div>
                  )}
                  {!isImage && !isPdf && !isCsv && (
                    <div className="text-center p-8">
                      <p className="text-jp-muted text-sm">Unable to preview this file type</p>
                      <a
                        href={docUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-block px-4 py-2 rounded-full bg-cyan-600 text-xs text-white hover:bg-cyan-500 transition-colors"
                      >
                        Open in New Tab
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default PendingApprovalRow;
